import { Box, Flex } from '@/components';
import { useAnalysisPageSearchQueryStates } from '@/features/analysis/_hooks/search-query-states';
import type { ProjectAnalysisModels } from '@/features/analysis/_models/project';
import {
  checkChildrenRecursively,
  getGraphOptions,
  getLineGraphOptions,
  getSeriesData,
} from '@/features/analysis/_utils';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { useLocaledAmount } from '@/providers';
import { BigCalculator } from '@/utils';
import type { SortOrder } from 'antd/es/table/interface';
import Highcharts from 'highcharts';
import {
  HighchartsReact,
  type HighchartsReactRefObject,
} from 'highcharts-react-official';
import TreemapModule from 'highcharts/modules/treemap';
import groupBy from 'lodash-es/groupBy';
import { type FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import type { z } from 'zod';
import { CustomLegend } from './CustomLegend';
import { GraphYearSelectorTabs } from './GraphYearSelectorTabs';

TreemapModule(Highcharts);

type Props = {
  graphData: z.infer<typeof ProjectAnalysisModels>;
  handleGraphInteraction: (ids: string[]) => Promise<void>;
  options:
    | {
        ids: string[];
        names: string[];
      }
    | undefined;
};

export const VisualizerContent: FC<Props> = memo(
  ({ graphData, handleGraphInteraction, options }) => {
    const { unit } = useLocaledAmount();
    const { searchQueryStates } = useAnalysisPageSearchQueryStates();
    const { order, graphType, analysisAxis } = searchQueryStates;
    const {
      cascaderTargetIds: {
        organizationIds: cascaderOrganizationIds,
        policyIds: cascaderPolicyIds,
      },
      data: {
        organizationsOptions: allOrganizationsOptions,
        policiesOptions: allPoliciesOptions,
      },
    } = useProjectsSearchStore();

    const highchartsRef = useRef<HighchartsReactRefObject | null>(null);
    const fiscalYears = graphData.map((item) => item.fiscalYear);

    const [selectedYear, setSelectedYear] = useState<number | null>(null);

    const handlePointClick = useCallback(
      ({ id }: { id: string }) => {
        // ドリルダウン中（単一の現在地あり）は、表示中の子要素を現在地パスに追加する
        if (options?.ids.length) {
          handleGraphInteraction([...options.ids, id]);
          return;
        }
        // 複数選択時（現在地なし）はクリック対象のフルパスを選択済みパスから解決する。
        // [id] のみを渡すと QueryStore に部分パスが保存され検索フォームの復元が壊れるため。
        const targetCascaderIds =
          analysisAxis === 'policy'
            ? cascaderPolicyIds
            : cascaderOrganizationIds;
        const fullPath = targetCascaderIds?.find((ids) => ids.at(-1) === id);
        handleGraphInteraction(fullPath ?? [id]);
      },
      [
        handleGraphInteraction,
        options,
        analysisAxis,
        cascaderPolicyIds,
        cascaderOrganizationIds,
      ],
    );

    const groupDataByAnalysisAxisId = groupBy(graphData, 'analysisAxis.id');
    const isPieOrTreeMapGraph = ['pieChart', 'treeMap'].includes(graphType);

    const currentId = options?.ids?.at(-1) ?? null;

    const sortedSeriesData = useMemo(() => {
      const seriesData = getSeriesData({
        data: groupDataByAnalysisAxisId,
        handlePointClick,
        currentId,
      });

      const orderingYear = order?.split('_')[1];
      if (!order || !orderingYear) {
        return seriesData;
      }

      if (order.includes('ascend' satisfies SortOrder)) {
        return seriesData.sort((a, b) => {
          const valueA =
            a.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
          const valueB =
            b.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
          return BigCalculator.subtract(valueA, valueB);
        });
      }

      return seriesData.sort((a, b) => {
        const valueA =
          a.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
        const valueB =
          b.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
        return BigCalculator.subtract(valueB, valueA);
      });
    }, [order, groupDataByAnalysisAxisId, handlePointClick, currentId]);

    const filteredSeriesData = useMemo(() => {
      if (isPieOrTreeMapGraph && selectedYear) {
        return sortedSeriesData
          .map((item) => {
            return {
              ...item,
              data: item.data.filter((d) => d.x === selectedYear),
            };
          })
          .filter((s) => s.data.length);
      }
      return sortedSeriesData;
    }, [isPieOrTreeMapGraph, selectedYear, sortedSeriesData]);

    const minYear = selectedYear ?? Math.min(...fiscalYears);
    const [isChartReady, setIsChartReady] = useState(false);

    const legendData = useMemo(() => {
      return filteredSeriesData.map(({ name, color, custom }) => {
        const { id = null, parentId } = custom;

        const itemId = parentId ?? id ?? '';

        // 分析軸に応じて適切なcascaderデータとオプションを使用
        const isPolicy = analysisAxis === 'policy';
        const targetCascaderIds = isPolicy
          ? cascaderPolicyIds
          : cascaderOrganizationIds;
        const allOptionsData = isPolicy
          ? allPoliciesOptions
          : allOrganizationsOptions;

        const targetNestedIds = targetCascaderIds
          ?.filter((ids) => ids.includes(itemId))
          .at(0);

        return {
          name: name.toString(),
          color,
          id,
          enableDrillDown:
            id !== currentId &&
            checkChildrenRecursively(
              targetNestedIds ?? [itemId],
              allOptionsData ?? [],
            ),
          onClick: () => {
            if (id) {
              handlePointClick({ id });
            }
          },
        };
      });
    }, [
      filteredSeriesData,
      analysisAxis,
      cascaderPolicyIds,
      cascaderOrganizationIds,
      allPoliciesOptions,
      allOrganizationsOptions,
      currentId,
      handlePointClick,
    ]);

    return (
      <Flex gap={8} align="center">
        <CustomLegend
          highchartsRef={highchartsRef}
          legendData={legendData}
          graphType={graphType}
          visibilitySearchQueryStates={
            searchQueryStates as unknown as Record<string, unknown>
          }
        />
        <Box width="80%">
          {isPieOrTreeMapGraph && (
            <Flex align="center" justify="center">
              <GraphYearSelectorTabs
                selectedYear={minYear}
                fiscalYears={fiscalYears}
                onSelect={setSelectedYear}
              />
            </Flex>
          )}
          <HighchartsReact
            ref={highchartsRef}
            highcharts={Highcharts}
            callback={() => {
              // チャートが描画された後に一度renderingを走らせないとhighchartsRefの取得ができず、CustomLegendの表示・非表示切り替えが正常に動作しない。
              if (!isChartReady) {
                setIsChartReady(true);
              }
            }}
            options={getLineGraphOptions({
              graphData,
              graphOptions: getGraphOptions({
                graphType,
                seriesData: sortedSeriesData,
                data: groupDataByAnalysisAxisId,
                selectedYear: minYear,
                unit,
                handlePointClick,
                currentId,
              }),
              graphType,
              unit,
            })}
          />
        </Box>
      </Flex>
    );
  },
);
VisualizerContent.displayName = 'VisualizerContent';
